import { z } from "zod";
import type { infer as zInfer } from "zod";
import { outcome } from "@/hooks/firestore/models/outcome";
import { timestamp } from "@/hooks/firestore/models/timestamp";
import { promotionCampaign } from "./promotionCampaign";

export const bettingEventMarketStatus = z.enum([
  "ACTIVE",
  "SUSPENDED",
  "HANDED_OVER",
  "SETTLED",
  "CANCELLED",
  "DEACTIVATED",
  "UNAVAILABLE",
  "INVALID",
  "LIVE",
]);

export const racingMarketType = z.enum([
  "EXACTA",
  "FIRST_4",
  "QUINELLA",
  "TRIFECTA",
  "RACING_PLACE",
  "RACING_PLACE_SP",
  "RACING_TOP_2_SP",
  "RACING_TOP_3",
  "RACING_TOP_3_SP",
  "RACING_WINNER",
  "RACING_WINNER_SP",
  "RACING_TOP_2",
  "RACING_TOP_4",
  "RACING_TOP_4_SP",
  "CUSTOM",
]);

// per-attribute display data (e.g. "period", "subPeriod") - human-readable
// phrase derived from the market's raw attributes (e.g. "1st Half")
export const marketRenderField = z.object({
  phrase: z.string().optional(),
});

export const market = z.object({
  id: z.string(),
  attributes: z.record(z.string(), z.any()).nullish(),
  renderFields: z.record(z.string(), marketRenderField).nullish(),
  allowedSelectionTypes: z.array(
    z.enum(["SingleOutcome", "SameEventMulti", "Exotic", "Quaddie"]),
  ),
  category: z.string().nullish(),
  categoryId: z.string().nullish(),
  hasPromotionalMarkets: z.boolean().optional(),
  bettingType: z.enum(["Prematch", "Live"]),
  // Live market that opened as prematch - still viewable without live access
  prematchMarket: z.boolean().optional(),
  blockPromotionsBets: z.boolean().optional(),
  campaignOnly: z.boolean(),
  campaignIds: z.array(z.string()).optional(),
  independentPerEvent: z.boolean().optional(),
  marketType: z.union([racingMarketType, z.string()]),
  name: z.string(),
  isCashoutAvailable: z.boolean().optional(),
  outcomes: z.record(z.string(), outcome),
  promotional: z.boolean(),
  specifiers: z.record(z.string(), z.string()).optional(),
  status: bettingEventMarketStatus,
  nextBetStop: z.union([timestamp, z.string()]).optional(),
  promotionCampaigns: z.array(promotionCampaign).nullish(),
});

export type Market = zInfer<typeof market>;
export type BettingMarketStatus = zInfer<typeof bettingEventMarketStatus>;
export type RacingMarketType = zInfer<typeof racingMarketType>;
